import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import type { IExtendedSearchItem } from "src/api/client/extended-search.api.types";
import { SEARCH_TABLE_FIRST_COLUMN_WIDTH } from "src/components/pages/search/components/table/consts";
import { ISearchTableFigure } from "src/components/pages/search/components/table/search-table.types";
import { TableScrollableCell } from "src/components-mui/base/table-scrollable/components/cell/table-scrollable-cell";
import { TableScrollableHeaderCell } from "src/components-mui/base/table-scrollable/components/cell/table-scrollable-header-cell";
import { TableCellSkeleton } from "src/components-mui/base/table-scrollable/components/skeleton/table-cell-skeleton";
import { TableEntitySkeleton } from "src/components-mui/base/table-scrollable/components/skeleton/table-entity-skeleton";
import { Typography } from "src/components-mui/base/typography/typography";
import { HeaderCellTitleLabel } from "src/components-mui/modules/wikifolio-table/components/header-cell-title-label";

interface IMakeSearchTableLoadingColumnsParams {
    figures: ISearchTableFigure[];
    disableAnimation?: boolean;
    sorting?: boolean;
    skeletonBackgroundColor?: string;
}

// https://github.com/TanStack/table/issues/4382#issuecomment-1571563559
export const makeSearchTableLoadingColumns = ({
    figures,
    disableAnimation = false,
    sorting = false,
    skeletonBackgroundColor,
}: // eslint-disable-next-line @typescript-eslint/no-explicit-any
IMakeSearchTableLoadingColumnsParams): ColumnDef<IExtendedSearchItem, any>[] => {
    const columnHelper = createColumnHelper<IExtendedSearchItem>();
    const animation = disableAnimation ? false : "pulse";

    return [
        columnHelper.accessor("trader", {
            enableSorting: false,
            header: ({ header }) => (
                <TableScrollableHeaderCell width={SEARCH_TABLE_FIRST_COLUMN_WIDTH} header={header} shrinkable>
                    <HeaderCellTitleLabel />
                </TableScrollableHeaderCell>
            ),
            cell: () => (
                <TableScrollableCell
                    width={SEARCH_TABLE_FIRST_COLUMN_WIDTH}
                    shrinkable
                    contentSx={{ justifyContent: ["space-between", "space-between", "initial"], paddingRight: [1, 1, 0] }}
                >
                    <TableEntitySkeleton animation={animation} backgroundColor={skeletonBackgroundColor} />
                </TableScrollableCell>
            ),
        }),
        ...figures.map(figure => {
            const isSortingEnabled = figure.orderDescending !== null;
            return columnHelper.accessor(row => row.figures?.find(r => r.id === figure.id), {
                id: figure.id,
                enableSorting: sorting && isSortingEnabled,
                header: ({ header }) => (
                    <TableScrollableHeaderCell header={header} tooltip={figure.tooltip?.content}>
                        <Typography
                            dangerouslySetInnerHTML={{ __html: figure.fullLabel }}
                            sx={{
                                fontWeight: "bold",
                                fontSize: "xs",
                            }}
                        />
                    </TableScrollableHeaderCell>
                ),
                cell: () => (
                    <TableScrollableCell>
                        <TableCellSkeleton animation={animation} backgroundColor={skeletonBackgroundColor} />
                    </TableScrollableCell>
                ),
            });
        }),

        // Custom follow column with empty header — skeleton body cell
        columnHelper.display({
            id: "follow",
            enableSorting: false,
            header: () => <TableScrollableHeaderCell sx={{ minWidth: "unset", width: "72px", paddingX: [1, 1, 1, 2] }}></TableScrollableHeaderCell>,
            cell: () => (
                <TableScrollableCell sx={{ minWidth: "unset", width: "72px", paddingX: [1, 1, 1, 2] }}>
                    <TableCellSkeleton animation={animation} backgroundColor={skeletonBackgroundColor} />
                </TableScrollableCell>
            ),
        }),
    ];
};
